import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { linkify } from "@/components/ExpandableText";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Share2, Eye, Repeat2, Loader2, Tag } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import PostCarousel from "@/components/PostCarousel";
import FullscreenImageViewer from "@/components/FullscreenImageViewer";
import RepostOriginal from "@/components/RepostOriginal";
import LinkPreviewCard from "@/components/LinkPreviewCard";
import PostReactions from "@/components/PostReactions";
import CommentThread from "@/components/CommentThread";
import PostMenu from "@/components/PostMenu";
import AuthorBadges from "@/components/AuthorBadges";
import { MusicTag, LocationTag } from "@/components/MusicLocation";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TaggedUsers({ users, testid }) {
  if (!users?.length) return null;
  return (
    <span className="text-sm text-muted-foreground" data-testid={testid}>
      {" "}— com{" "}
      {users.map((u, i) => (
        <React.Fragment key={u.user_id}>
          {i > 0 && (i === users.length - 1 ? " e " : ", ")}
          <Link to={`/user/${u.user_id}`} className="font-semibold text-foreground hover:underline" data-testid={`tagged-user-${u.user_id}`}>{u.name}</Link>
        </React.Fragment>
      ))}
    </span>
  );
}

export default function PostDetail() {
  const { postId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(null);
  const highlightComment = params.get("comment");

  useEffect(() => {
    setPost(null); setError(null);
    axios.get(`${API}/posts/${postId}`)
      .then((r) => setPost(r.data))
      .catch((e) => setError(e.response?.status === 403 ? "Este post é privado." : "Post não encontrado ou removido."));
  }, [postId]);

  useEffect(() => {
    if (!post || !highlightComment) return;
    const el = document.querySelector(`[data-testid="comment-${highlightComment}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "rounded-xl");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary", "rounded-xl"), 3500);
    }
  }, [post, highlightComment]);

  const share = async () => {
    try {
      const r = await axios.post(`${API}/posts/${post.post_id}/repost`, { comment: "" });
      if (r.data?.already) toast.info("Você já compartilhou este post");
      else { toast.success("Compartilhado no feed para todos!"); setPost((p) => ({ ...p, share_count: (p.share_count || 0) + 1 })); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao compartilhar"); }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="post-detail-error">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-3"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
        <Card className="p-10 text-center text-muted-foreground">{error}</Card>
      </div>
    );
  }
  if (!post) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const p = post;
  const gallery = Array.isArray(p.image_urls) && p.image_urls.length ? p.image_urls : (p.image_url ? [p.image_url] : []);
  const isMe = user?.user_id === p.user_id;

  return (
    <div className="max-w-2xl mx-auto space-y-3" data-testid="post-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="post-detail-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
      </Button>
      <Card className="p-5 ring-2 ring-primary/30" data-testid={`post-detail-${p.post_id}`}>
        {p.is_repost && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 pb-2 border-b border-border/60">
            <Repeat2 className="w-4 h-4" />
            <Link to={`/user/${p.user_id}`} className="font-semibold hover:underline">{p.author_name}</Link>
            <span>compartilhou</span>
            {p.original_user_name && <><span>de</span><Link to={`/user/${p.original_user_id}`} className="font-semibold hover:underline">{p.original_user_name}</Link></>}
          </div>
        )}
        <div className="flex items-start gap-3 mb-4">
          <Link to={`/user/${p.user_id}`}>
            <Avatar className="w-11 h-11"><AvatarImage src={p.author_picture} /><AvatarFallback>{p.author_name?.[0]}</AvatarFallback></Avatar>
          </Link>
          <div className="flex-1">
            <div>
              <Link to={`/user/${p.user_id}`} className="font-semibold hover:underline" data-testid="post-detail-author">{p.author_name}</Link>
              <AuthorBadges badges={p.author_badges} testid="post-detail-author-badges" />
              <TaggedUsers users={p.tagged_users} testid="post-detail-tagged" />
            </div>
            <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground"><Eye className="w-3.5 h-3.5" /> {p.views || 0}</div>
          <PostMenu post={p} user={user} onEdited={(u) => setPost((x) => ({ ...x, ...u }))} onDeleted={() => navigate("/feed")} onHidden={() => navigate("/feed")} />
        </div>
        {p.content && <p className="text-base whitespace-pre-wrap mb-3 break-words" data-testid="post-detail-content">{linkify(p.content, { mentions: p.mentions })}</p>}
        {!p.is_repost && (p.music || p.location) && (
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="post-detail-tags">
            <MusicTag music={p.music} testid="post-detail-music" />
            <LocationTag location={p.location} testid="post-detail-location" />
          </div>
        )}
        {p.is_repost && <RepostOriginal p={p} testidPrefix="pd-repost" />}
        {!p.is_repost && p.link_preview && <LinkPreviewCard preview={p.link_preview} testid="post-detail-link" />}
        {!p.is_repost && gallery.length > 0 && <PostCarousel urls={gallery} testidPrefix={`pd-carousel-${p.post_id}`} onOpenFullscreen={(pl) => pl?.isImage && setFullscreen({ url: pl.url, urls: pl.urls || null, index: pl.index || 0 })} />}
        {!p.is_repost && p.video_url && <video src={p.video_url} poster={p.cover_url || undefined} controls playsInline className="w-full max-h-[70vh] rounded-xl bg-black mb-3" />}
        <div className="flex items-center justify-between text-sm text-muted-foreground pt-3 border-t border-border">
          <PostReactions postId={p.post_id} initialReactions={p.reactions} initialMy={p.my_reaction} initialTotal={p.reactions_total ?? (p.likes?.length || 0)} />
          <span className="flex items-center gap-2"><MessageCircle className="w-5 h-5" /> {(p.comments || []).length}</span>
          {!isMe && (
            <button onClick={share} className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="post-detail-share">
              <Share2 className="w-5 h-5" /> {p.share_count || 0}
            </button>
          )}
        </div>
        <div className="mt-4" data-testid="post-detail-comments">
          <CommentThread
            postId={p.post_id}
            comments={p.comments || []}
            onNewComment={(c) => setPost((x) => ({ ...x, comments: [...(x.comments || []), c] }))}
            onLocalUpdate={(u) => setPost((x) => ({ ...x, comments: (x.comments || []).map((c) => c.comment_id === u.comment_id ? u : c) }))}
            onDeleted={(id, removed) => setPost((x) => ({ ...x, comments: (x.comments || []).filter((c) => c.comment_id !== id && c.parent_comment_id !== id) }))}
            onEdited={(u) => setPost((x) => ({ ...x, comments: (x.comments || []).map((c) => c.comment_id === u.comment_id ? u : c) }))}
          />
        </div>
      </Card>
      <FullscreenImageViewer src={fullscreen?.url} images={fullscreen?.urls} startIndex={fullscreen?.index || 0} music={p.music} onClose={() => setFullscreen(null)} />
    </div>
  );
}
